import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProjectDto, useListContractTemplatesQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { Logo } from "../../../common/Logo";
import { ModalSkeleton } from "../../../common/ModalSkeleton";
import { useSub } from "../../../common/subscription/useSub";
import { CreateContractForm } from "./CreateContractForm";
import { CreateContractWithAIForm } from "./CreateContractWithAIForm";

interface Props {
  project?: ProjectDto;
  onClose: () => void;
  goToContractOnCreation?: boolean;
}

type FormType = "manual" | "ai";

export const CreateContractModal = ({ project, onClose, goToContractOnCreation = true }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const { t } = useTranslation();
  const sub = useSub();
  const [formType, setFormType] = useState<FormType>("manual");

  const { data: templatesRes } = useListContractTemplatesQuery(
    {
      organizationId: authState.selectedOrg.id,
      orgId: authState.selectedOrg.id,
    },
    { refetchOnFocus: true },
  );

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          {t("New contract")} {project ? `${t("connected to the project")} ${project.name}` : ""}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {templatesRes ? (
            <>
              {sub.hasAiAccess && (
                <Flex width="full" justifyContent={"center"}>
                  <Flex>
                    <Button
                      roundedRight="none"
                      variant={formType === "manual" ? "solid" : "ghost"}
                      colorScheme={"teal"}
                      rounded={"md"}
                      border="1px solid teal"
                      onClick={() => setFormType("manual")}
                    >
                      {t("Manual")}
                    </Button>
                    <Button
                      roundedLeft="none"
                      variant={formType === "ai" ? "solid" : "ghost"}
                      overflow={"hidden"}
                      colorScheme={"teal"}
                      rounded={"md"}
                      border="1px solid teal"
                      borderLeft={"none"}
                      rightIcon={<Logo />}
                      onClick={() => setFormType("ai")}
                    >
                      {t("AI")}
                    </Button>
                  </Flex>
                </Flex>
              )}
              {formType === "manual" && (
                <CreateContractForm
                  onClose={onClose}
                  project={project}
                  templates={templatesRes.templates}
                  goToContractOnCreation={goToContractOnCreation}
                />
              )}
              {formType === "ai" && <CreateContractWithAIForm project={project} templates={templatesRes.templates} />}
            </>
          ) : (
            <ModalSkeleton />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
